import { Signal } from "@engine";
import type { StoryProgressService } from "../services/StoryProgressService";
import type { DifficultyService } from "../services/DifficultyService";
import type { BestScoresService } from "../services/BestScoresService";
import type { DifficultyId } from "../config/difficulty";
import { LEVELS, STORY_LEVEL_NAMES } from "../level/levels";

// Renders the per-difficulty Story Level grid in the #hud-story-list
// container. Owned by LevelSelectScreen; HUD.setState("story-select")
// just shows/hides the parent container, this module fills it.
//
// show() subscribes to DifficultyService.onChanged + StoryProgressService
// .onChanged so the grid re-renders the moment the player flips a
// difficulty pill (changes which cards are locked) or comes back from
// a freshly-cleared run (unlocks a new card while the screen is
// already open).
export class LevelSelectScreen {
  // Fired with a 1-indexed level number when the player clicks an
  // UNLOCKED card. MorphHexGame routes this to startGame("story", N-1).
  // Locked clicks shake the card in-place and emit nothing.
  readonly onSelect = new Signal<number>();

  private list: HTMLElement;
  private progress: StoryProgressService;
  private difficulty: DifficultyService;
  private bests: BestScoresService;
  private diffUnsub: (() => void) | null = null;
  private progressUnsub: (() => void) | null = null;

  constructor(
    progress: StoryProgressService,
    difficulty: DifficultyService,
    bests: BestScoresService
  ) {
    const list = document.getElementById("hud-story-list");
    if (!list) throw new Error("LevelSelectScreen: #hud-story-list not found");
    this.list = list;
    this.progress = progress;
    this.difficulty = difficulty;
    this.bests = bests;

    // Card click handler. We only emit onSelect for unlocked cards;
    // locked cards trigger a one-shot CSS shake so the player knows
    // the click was registered but ignored.
    this.list.addEventListener("click", (ev) => {
      const card = (ev.target as HTMLElement).closest(
        "button[data-level]"
      ) as HTMLElement | null;
      if (!card) return;
      const level = Number(card.dataset["level"]);
      if (!Number.isFinite(level) || level < 1) return;
      const diff = this.difficulty.getId();
      if (this.progress.isUnlocked(diff, level)) {
        this.onSelect.emit(level);
        return;
      }
      // Locked: replay the shake animation by removing + re-adding the
      // class on the next frame so consecutive clicks always animate.
      card.classList.remove("shake");
      void card.offsetWidth;
      card.classList.add("shake");
    });
  }

  show(): void {
    this.render();
    // Re-render whenever the player flips difficulty inside the
    // selector (the difficulty pill row is wired to the same global
    // service, so changing the pill flips which cards are locked).
    if (!this.diffUnsub) {
      this.diffUnsub = this.difficulty.onChanged.on(() => this.render());
    }
    if (!this.progressUnsub) {
      this.progressUnsub = this.progress.onChanged.on(() => this.render());
    }
  }

  hide(): void {
    this.diffUnsub?.();
    this.diffUnsub = null;
    this.progressUnsub?.();
    this.progressUnsub = null;
  }

  // Build all six level cards from scratch. Cheap (a few dozen DOM
  // nodes) and only called on show() / difficulty change / unlock,
  // so we don't bother diffing.
  private render(): void {
    const diff = this.difficulty.getId();
    const cards: HTMLElement[] = [];
    for (let i = 0; i < LEVELS.length; i++) {
      cards.push(this.makeCard(i + 1, diff));
    }
    this.list.replaceChildren(...cards);
  }

  private makeCard(level: number, diff: DifficultyId): HTMLElement {
    const card = document.createElement("button");
    card.type = "button";
    card.dataset["level"] = String(level);
    const unlocked = this.progress.isUnlocked(diff, level);
    card.className = `level-card ${unlocked ? "" : "locked"}`.trim();
    const name = STORY_LEVEL_NAMES[level - 1] ?? `Level ${level}`;
    const best =
      this.bests.get(`story:${level}:${diff}`)?.score ?? null;
    const meta = unlocked
      ? best != null
        ? `Best ${best.toLocaleString()}`
        : "Tap to play"
      : "Locked";
    card.innerHTML = `
      <div class="num"></div>
      <div class="name"></div>
      <div class="meta"></div>
      ${unlocked ? "" : '<div class="lock" aria-hidden="true">&#128274;</div>'}
    `;
    (card.querySelector(".num") as HTMLElement).textContent = `LEVEL ${level}`;
    (card.querySelector(".name") as HTMLElement).textContent = name;
    (card.querySelector(".meta") as HTMLElement).textContent = meta;
    return card;
  }
}
